// CreateSectionModal — the Groups tab's section name dialog, doing double duty:
// `section` absent → "New section" (POST /group-sections); `section` present →
// rename it (PATCH /group-sections/:id). Same single-field form either way; the
// same create-or-edit shape as form-templates' ResponseSetModal. Cache
// invalidation refetches the sections list.

import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Input, Modal } from '@/components/common';
import {
  useCreateGroupSectionMutation,
  useUpdateGroupSectionMutation,
  type GroupSection,
} from '@/api/endpoints/groupsApi';

interface CreateSectionModalProps {
  open: boolean;
  /** Present → rename this section instead of creating a new one. */
  section?: GroupSection | null;
  onClose: () => void;
}

const CreateSectionModal = ({ open, section = null, onClose }: CreateSectionModalProps) => {
  const { t } = useTranslation();
  const [createSection, { isLoading: creating }] = useCreateGroupSectionMutation();
  const [updateSection, { isLoading: renaming }] = useUpdateGroupSectionMutation();
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const editing = !!section;
  const isLoading = creating || renaming;

  // Keyed on `section` too — reopening for a different section must reseed.
  useEffect(() => {
    if (open) {
      setName(section?.name ?? '');
      setSubmitted(false);
    }
  }, [open, section]);

  const nameMissing = !name.trim();

  const submit = async () => {
    setSubmitted(true);
    if (nameMissing || isLoading) return;
    try {
      if (section)
        await updateSection({ id: section.id, body: { sectionName: name.trim() } }).unwrap();
      else await createSection({ sectionName: name.trim() }).unwrap();
      toast.success(t(section ? 'section_updated' : 'section_created'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={editing ? 'folder-pen' : 'folder-plus'}
      title={t(editing ? 'section_rename_title' : 'section_create_title')}
      size='sm'
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={editing ? 'check' : 'plus'} onClick={submit} loading={isLoading}>
            {t(editing ? 'section_rename_confirm' : 'groups_new_section')}
          </Button>
        </>
      }
    >
      <Field
        label={t('section_field_name')}
        required
        error={submitted && nameMissing ? t('section_name_required') : undefined}
      >
        <Input
          autoFocus
          value={name}
          placeholder={t('section_field_name_ph')}
          error={submitted && nameMissing}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </Field>
    </Modal>
  );
};

export default CreateSectionModal;
